import React, { useState, useEffect, useRef } from 'react';
import { API_URL, LOCATION_ID } from '..';
import Quill from 'quill';
import 'quill/dist/quill.snow.css';

const AddSupplierLayers = () => {
  const [formValues, setFormValues] = useState({
    name: '',
    contact_person: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    country: '',
    tax_number: '',
    opening_balance:'',
    total_amount:'',
    description: '',
    locationId: LOCATION_ID
  });

  const [message, setMessage] = useState({
    status: false,
    reason: '',
    desc: ''
  });

  const quillRef = useRef(null);
  const quillInstance = useRef(null);

  // Initialize Quill editor
  useEffect(() => {
    if (quillRef.current && !quillInstance.current) {
      quillInstance.current = new Quill(quillRef.current, {
        theme: 'snow',
        modules: {
          toolbar: [
            ['bold', 'italic', 'underline'],
            [{ list: 'ordered' }, { list: 'bullet' }],
            [{ align: '' }, { align: 'center' }, { align: 'right' }],
          ],
        },
        placeholder: 'Enter supplier notes or description',
      });

      // Ensure text direction and language
      quillInstance.current.root.setAttribute('dir', 'ltr');
      quillInstance.current.root.setAttribute('lang', 'en');

      // Handle content changes
      quillInstance.current.on('text-change', () => {
        const content = quillInstance.current.root.innerHTML;
        setFormValues((prev) => ({ ...prev, description: content }));
      });

      quillInstance.current.focus();
    }

    return () => {
      if (quillInstance.current) {
        quillInstance.current.off('text-change');
        quillInstance.current = null;
      }
    };
  }, []);

  // Update Quill when description changes
  useEffect(() => {
    if (quillInstance.current && formValues.description) {
      if (quillInstance.current.root.innerHTML !== formValues.description) {
        quillInstance.current.root.innerHTML = formValues.description;
        quillInstance.current.focus();
      }
    }
  }, [formValues.description]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formValues.name) {
      setMessage({ status: true, reason: 'error', desc: 'Supplier name is required' });
      return;
    }
    if (!formValues.phone) {
      setMessage({ status: true, reason: 'error', desc: 'Phone number is required' });
      return;
    }
    if (!formValues.address) {
      setMessage({ status: true, reason: 'error', desc: 'Address is required' });
      return;
    }
    // if (!formValues.description || formValues.description === '<p><br></p>') {
    //   setMessage({ status: true, reason: 'error', desc: 'Description is required' });
    //   return;
    // }

    try {
      const response = await fetch(`${API_URL}/api/suppliers`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formValues),
      });

      const data = await response.json();

      if (response.status === 201 || response.status === 200) {
        setMessage({ status: true, reason: 'success', desc: 'Supplier added successfully' });
        setFormValues({
          name: '',
        //   contact_person: '',
          email: '',
          phone: '',
          address: '',
          total_amount:'',
          city: '',
          opening_balance:'',
          country: '',
          tax_number: '',
          description: '',
          locationId: LOCATION_ID
        });

        if (quillInstance.current) {
          quillInstance.current.root.innerHTML = '';
          quillInstance.current.focus();
        }
      } else {
        throw new Error(data.message || 'Failed to add supplier');
      }
    } catch (error) {
      setMessage({ status: true, reason: 'error', desc: `Failed to add supplier: ${error.message}` });
    }
  };

  return (
    <div className="card h-100 p-0 radius-12">
      <div className="card-body p-24">
        <style>
          {`
            .ql-container {
              font-family: Arial, sans-serif;
              font-size: 14px;
              min-height: 150px;
              border: 1px solid #ced4da;
              border-radius: 8px;
              direction: ltr;
            }
            .ql-container.is-invalid {
              border-color: #dc3545;
            }
            .ql-editor {
              min-height: 120px;
              padding: 10px;
            }
            .ql-toolbar {
              border-bottom: 1px solid #ced4da;
              border-top-left-radius: 8px;
              border-top-right-radius: 8px;
            }
            .ql-editor:focus {
              outline: none;
              box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
            }
          `}
        </style>
        <div className="row justify-content-center">
          <div className="col-xxl-6 col-xl-8 col-lg-10">
            <div className="card border">
              <div className="card-body">
                {/* Alert */}
                {message.status && (
                  <div
                    className={`alert alert-${message.reason === 'error' ? 'danger' : 'success'} alert-dismissible fade show mb-20`}
                    role="alert"
                  >
                    {message.desc}
                    <button
                      type="button"
                      className="btn-close"
                      onClick={() => setMessage({ ...message, status: false })}
                      aria-label="Close"
                    ></button>
                  </div>
                )}

                {/* Form */}
                <form onSubmit={handleSubmit}>
                  <div className="mb-20">
                    <label htmlFor="name" className="form-label fw-semibold text-primary-light text-sm mb-8">
                      Supplier Name <span className="text-danger-600">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control radius-8"
                      id="name"
                      value={formValues.name}
                      onChange={(e) => setFormValues((prev) => ({ ...prev, name: e.target.value }))}
                      placeholder="Enter supplier name"
                    />
                  </div>

                  <div className="mb-20">
                    <label htmlFor="total_amount" className="form-label fw-semibold text-primary-light text-sm mb-8">
                      Opening Balance
                    </label>
                    <input
                      type="text"
                      className="form-control radius-8"
                      id="total_amount"
                      value={formValues.opening_balance}
                      onChange={(e) => setFormValues((prev) => ({ ...prev, opening_balance: e.target.value }))}
                      placeholder="Opening Balance"
                    />
                  </div>

                  <div className="mb-20">
                    <label htmlFor="email" className="form-label fw-semibold text-primary-light text-sm mb-8">
                      Email
                    </label>
                    <input
                      type="email"
                      className="form-control radius-8"
                      id="email"
                      value={formValues.email}
                      onChange={(e) => setFormValues((prev) => ({ ...prev, email: e.target.value }))}
                      placeholder="Enter supplier email"
                    />
                  </div>

                  <div className="mb-20">
                    <label htmlFor="phone" className="form-label fw-semibold text-primary-light text-sm mb-8">
                      Phone <span className="text-danger-600">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control radius-8"
                      id="phone"
                      value={formValues.phone}
                      onChange={(e) => setFormValues((prev) => ({ ...prev, phone: e.target.value }))}
                      placeholder="Enter phone number"
                    />
                  </div>

                  <div className="mb-20">
                    <label htmlFor="address" className="form-label fw-semibold text-primary-light text-sm mb-8">
                      Address <span className="text-danger-600">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control radius-8"
                      id="address"
                      value={formValues.address}
                      onChange={(e) => setFormValues((prev) => ({ ...prev, address: e.target.value }))}
                      placeholder="Enter supplier address"
                    />
                  </div>

                  <div className="mb-20">
                    <label htmlFor="city" className="form-label fw-semibold text-primary-light text-sm mb-8">
                      City
                    </label>
                    <input
                      type="text"
                      className="form-control radius-8"
                      id="city"
                      value={formValues.city}
                      onChange={(e) => setFormValues((prev) => ({ ...prev, city: e.target.value }))}
                      placeholder="Enter city"
                    />
                  </div>

                  <div className="mb-20">
                    <label htmlFor="country" className="form-label fw-semibold text-primary-light text-sm mb-8">
                      Country
                    </label>
                    <input
                      type="text"
                      className="form-control radius-8"
                      id="country"
                      value={formValues.country}
                      onChange={(e) => setFormValues((prev) => ({ ...prev, country: e.target.value }))}
                      placeholder="Enter country"
                    />
                  </div>

                  <div className="mb-20">
                    <label htmlFor="tax_number" className="form-label fw-semibold text-primary-light text-sm mb-8">
                      Tax Number
                    </label>
                    <input
                      type="text"
                      className="form-control radius-8"
                      id="tax_number"
                      value={formValues.tax_number}
                      onChange={(e) => setFormValues((prev) => ({ ...prev, tax_number: e.target.value }))}
                      placeholder="Enter tax number"
                    />
                  </div>

                  {/* <div className="mb-20">
                    <label htmlFor="description" className="form-label fw-semibold text-primary-light text-sm mb-8">
                      Notes / Description <span className="text-danger-600">*</span>
                    </label>
                    <div
                      ref={quillRef}
                      className={`ql-container ${message.reason === 'error' && (!formValues.description || formValues.description === '<p><br></p>') ? 'is-invalid' : ''}`}
                    />
                    <div className="form-text">
                      Add any additional supplier notes or description (supports bold, bullet points, etc.).
                    </div>
                    {message.reason === 'error' && (!formValues.description || formValues.description === '<p><br></p>') && (
                      <div style={{ color: '#dc3545', fontSize: '0.875em' }}>Description is required</div>
                    )}
                  </div> */}

                  <div className="d-flex align-items-center justify-content-center gap-3">
                    <button
                      type="submit"
                      className="btn btn-primary border border-primary-400 text-md px-56 py-12 radius-8"
                    >
                      Submit
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddSupplierLayers;
